// src/pages/Utilisateurs/PrepaPhysique/components/PrepaPhysiqueExerciseSheet.jsx

import React, { useEffect } from "react";
import {
  FaCalendarAlt,
  FaExternalLinkAlt,
  FaTimes,
  FaUser,
  FaYoutube,
} from "react-icons/fa";

import { FaRepeat } from "react-icons/fa6";

import {
  formatDateFR,
  getAdherentFullName,
  getExerciseDone,
  getExerciseProgramme,
  getExerciseRealisationDate,
  getExerciseRepetitions,
  toAbsoluteMediaUrl,
} from "../utils/prepaPhysiqueUtils";

import DoneSwitch from "./DoneSwitch";

const FALLBACK_IMG = "/img/no-image.jpg";

export default function PrepaPhysiqueExerciseSheet({
  isOpen,
  adherent,
  exercise,
  saving,
  onClose,
  onToggle,
}) {
  const done = getExerciseDone(exercise);
  const imageUrl = toAbsoluteMediaUrl(exercise?.lien_photo) || FALLBACK_IMG;
  const videoUrl = exercise?.lien_video || "";
  const programme = getExerciseProgramme(exercise);
  const repetitions = getExerciseRepetitions(exercise);
  const dateRealisation = getExerciseRealisationDate(exercise);
  const adherentName = getAdherentFullName(adherent);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !exercise) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-slate-950/65 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={exercise?.nom || "Exercice"}
    >
      <article
        className="absolute inset-x-0 bottom-0 max-h-[94dvh] overflow-hidden rounded-t-[2rem] bg-white shadow-2xl dark:bg-slate-900 sm:relative sm:inset-auto sm:w-full sm:max-w-3xl sm:rounded-[2rem]"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="relative overflow-hidden bg-gradient-to-r from-slate-950 via-blue-900 to-violet-700 px-4 py-4 text-white sm:px-6">
          <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-white/20 blur-2xl" />

          <div className="relative flex items-start justify-between gap-3">
            <div className="min-w-0">
              {programme && (
                <span className="inline-flex rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase ring-1 ring-white/20">
                  {programme}
                </span>
              )}

              <h2 className="mt-2 line-clamp-2 text-xl font-bold leading-tight sm:text-2xl">
                {exercise?.nom || "Exercice"}
              </h2>

              <p className="mt-1 flex items-center gap-2 text-sm text-white/85">
                <FaUser className="text-xs" />
                {adherentName || "Adhérent"}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur transition hover:bg-white/25 active:scale-95"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </header>

        <div className="max-h-[calc(94dvh-128px)] overflow-y-auto p-4 sm:p-6">
          <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
            <img
              src={imageUrl}
              alt={exercise?.nom || "Exercice"}
              className="max-h-[460px] w-full object-contain"
              onError={(event) => {
                event.currentTarget.src = FALLBACK_IMG;
              }}
            />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <InfoCard icon={FaRepeat} label="Répétitions" value={repetitions} />

            <InfoCard
              icon={FaCalendarAlt}
              label="Date"
              value={formatDateFR(dateRealisation)}
            />
          </div>

          {exercise?.description && (
            <div className="mt-4 rounded-[1.5rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                Consignes
              </p>

              <div
                className="mt-3 text-sm leading-6 text-slate-700 dark:text-slate-300
                [&_p]:mb-2 [&_p:last-child]:mb-0
                [&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5
                [&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5
                [&_strong]:font-bold [&_em]:italic
                [&_a]:text-blue-600 [&_a]:underline dark:[&_a]:text-blue-400"
                dangerouslySetInnerHTML={{ __html: exercise.description }}
              />
            </div>
          )}

          <div className="sticky bottom-0 -mx-4 mt-5 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:-mx-6 sm:px-6">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={onToggle}
                disabled={saving}
                className={`inline-flex min-h-12 items-center justify-center gap-3 rounded-2xl px-4 text-sm font-bold transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 ${
                  done
                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
                    : "bg-slate-950 text-white dark:bg-white dark:text-slate-950"
                }`}
              >
                <DoneSwitch
                  checked={done}
                  disabled={saving}
                  onChange={() => {}}
                  title=""
                />
                {done ? "Réalisé" : "À faire"}
              </button>

              {videoUrl ? (
                <a
                  href={videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 text-sm font-bold text-white transition active:scale-95"
                >
                  <FaYoutube />
                  Vidéo
                  <FaExternalLinkAlt className="text-[10px]" />
                </a>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 text-sm font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
                >
                  <FaTimes />
                  Fermer
                </button>
              )}
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
      <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
        <Icon />
        {label}
      </p>

      <p className="mt-1 text-sm font-bold text-slate-900 dark:text-white">
        {value || "—"}
      </p>
    </div>
  );
}